import { CONTRACT_TYPE_LABELS } from "@etudescarre/constants";
import {
  Button,
  Combobox,
  DataTable,
  type DataTableColumn,
  Modal,
} from "@etudescarre/ui";
import {
  EyeIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XIcon,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { ContractForm } from "./contract-form";

type Contract = {
  id: string;
  yardId: number;
  customerId: number | null;
  performerId: number;
  groupId: number | null;
  type: "amo" | "ct" | "sps" | "moe" | "travaux_n_1" | "travaux_n_2";
  shortName: string | null;
  isActive: boolean;
  isLead: boolean;
  delayForNoticeDays: number | null;
  createdAt: string;
  yard: { id: number; name: string };
  performer: { id: number; name: string };
  customer: { id: number; name: string } | null;
  group: { id: number; name: string } | null;
};

type EditableContract = {
  id: number;
  yardId: number;
  customerId: number | null;
  performerId: number;
  groupId: number | null;
  type: "amo" | "ct" | "sps" | "moe" | "travaux_n_1" | "travaux_n_2";
  shortName: string | null;
  isActive: boolean;
  isLead: boolean;
  delayForNoticeDays: number | null;
};

export function ContractsList() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingContract, setEditingContract] = useState<
    EditableContract | undefined
  >();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingContract, setDeletingContract] = useState<Contract | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [filterYard, setFilterYard] = useState<string[]>([]);
  const [filterPerformer, setFilterPerformer] = useState<string[]>([]);
  const [filterName, setFilterName] = useState("");

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    const { data, error } = await apiClient.GET("/api/admin/contracts");
    if (error) {
      setError("Erreur lors du chargement des contrats");
      setLoading(false);
      return;
    }

    setContracts(
      data.data.contracts.map((c) => ({
        ...c,
        id: String(c.id),
      })),
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const handleCreate = () => {
    setEditingContract(undefined);
    setDrawerOpen(true);
  };

  const handleEdit = (contract: Contract) => {
    setEditingContract({
      id: Number(contract.id),
      yardId: contract.yardId,
      customerId: contract.customerId,
      performerId: contract.performerId,
      groupId: contract.groupId,
      type: contract.type,
      shortName: contract.shortName,
      isActive: contract.isActive,
      isLead: contract.isLead,
      delayForNoticeDays: contract.delayForNoticeDays,
    });
    setDrawerOpen(true);
  };

  const handleDelete = (contract: Contract) => {
    setDeletingContract(contract);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingContract) return;

    setIsDeleting(true);
    const { error } = await apiClient.DELETE("/api/admin/contracts/{id}", {
      params: { path: { id: Number(deletingContract.id) } },
    });

    if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec de la suppression du contrat",
      });
      setIsDeleting(false);
      return;
    }

    toaster.success({
      title: "Succès",
      description: "Contrat supprimé avec succès",
    });

    setDeleteModalOpen(false);
    setDeletingContract(null);
    setIsDeleting(false);
    fetchContracts();
  };

  const handleFormSuccess = () => {
    fetchContracts();
  };

  const yardOptions = useMemo(() => {
    const unique = new Map<number, string>();
    for (const contract of contracts) {
      unique.set(contract.yard.id, contract.yard.name);
    }
    return [...unique.entries()]
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([id, name]) => ({ label: name, value: String(id) }));
  }, [contracts]);

  const performerOptions = useMemo(() => {
    const unique = new Map<number, string>();
    for (const contract of contracts) {
      unique.set(contract.performer.id, contract.performer.name);
    }
    return [...unique.entries()]
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([id, name]) => ({ label: name, value: String(id) }));
  }, [contracts]);

  const hasActiveFilters =
    filterYard.length > 0 || filterPerformer.length > 0 || filterName !== "";

  const clearAllFilters = () => {
    setFilterYard([]);
    setFilterPerformer([]);
    setFilterName("");
  };

  const filteredContracts = useMemo(() => {
    return contracts.filter((contract) => {
      if (
        filterYard.length > 0 &&
        !filterYard.includes(String(contract.yard.id))
      ) {
        return false;
      }
      if (
        filterPerformer.length > 0 &&
        !filterPerformer.includes(String(contract.performer.id))
      ) {
        return false;
      }
      if (filterName) {
        const search = filterName.toLowerCase();
        const matchesCode = contract.shortName?.toLowerCase().includes(search);
        const matchesPerformer = contract.performer.name
          .toLowerCase()
          .includes(search);
        const matchesYard = contract.yard.name.toLowerCase().includes(search);
        if (!matchesCode && !matchesPerformer && !matchesYard) {
          return false;
        }
      }
      return true;
    });
  }, [contracts, filterYard, filterPerformer, filterName]);

  const columns: DataTableColumn<Contract>[] = [
    {
      id: "type",
      header: "Type",
      accessor: (row) => CONTRACT_TYPE_LABELS[row.type],
      isSortable: true,
      loadingCell: "text",
    },
    {
      id: "shortName",
      header: "Code",
      accessor: (row) => row.shortName ?? "—",
      loadingCell: "text",
    },
    {
      id: "yard",
      header: "Chantier",
      accessor: (row) => row.yard.name,
      isSortable: true,
      loadingCell: "text",
    },
    {
      id: "performer",
      header: "Entreprise",
      accessor: (row) => row.performer.name,
      isSortable: true,
      loadingCell: "text",
    },
    {
      id: "group",
      header: "Groupe",
      accessor: (row) => row.group?.name ?? "—",
      loadingCell: "text",
    },
    {
      id: "isActive",
      header: "Actif",
      accessor: (row) => (row.isActive ? "Oui" : "Non"),
      loadingCell: "text",
    },
    {
      id: "actions",
      header: "Actions",
      accessor: () => "",
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            plain
            onClick={() => navigate(`/admin/contracts/${Number(row.id)}`)}
            aria-label="Détails"
          >
            <EyeIcon className="size-4" />
          </Button>
          <Button plain onClick={() => handleEdit(row)} aria-label="Modifier">
            <PencilIcon className="size-4" />
          </Button>
          <Button
            plain
            onClick={() => handleDelete(row)}
            aria-label="Supprimer"
          >
            <TrashIcon className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Contrats
          </h2>
          <Button onClick={handleCreate}>
            <PlusIcon data-slot="icon" className="size-4" />
            Créer un contrat
          </Button>
        </div>

        <div className="mb-4 flex items-end gap-3">
          <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label
                htmlFor="filter-name"
                className="mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300"
              >
                Recherche
              </label>
              <input
                id="filter-name"
                type="text"
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                placeholder="Code, chantier, entreprise…"
                className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
              />
            </div>
            <Combobox
              options={yardOptions}
              value={filterYard}
              onValueChange={({ value }) => setFilterYard(value)}
              placeholder="Tous les chantiers"
              label="Chantier"
            />
            <Combobox
              options={performerOptions}
              value={filterPerformer}
              onValueChange={({ value }) => setFilterPerformer(value)}
              placeholder="Toutes les entreprises"
              label="Entreprise"
            />
          </div>
          <Button
            plain
            onClick={clearAllFilters}
            disabled={!hasActiveFilters}
            aria-label="Réinitialiser les filtres"
          >
            <XIcon data-slot="icon" />
            Réinitialiser
          </Button>
        </div>

        <DataTable
          columns={columns}
          data={filteredContracts}
          isLoading={loading}
          isEmpty={filteredContracts.length === 0}
          emptyState={
            <div className="flex min-h-[200px] items-center justify-center text-gray-500">
              Aucun contrat trouvé
            </div>
          }
        />
      </div>

      <ContractForm
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        contract={editingContract}
        onSuccess={handleFormSuccess}
      />

      <Modal.Root
        open={deleteModalOpen}
        onOpenChange={(details) => setDeleteModalOpen(details.open)}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Confirmer la suppression</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Modal.Description>
                  Êtes-vous sûr de vouloir supprimer ce contrat (
                  {deletingContract
                    ? CONTRACT_TYPE_LABELS[deletingContract.type]
                    : ""}
                  {deletingContract?.shortName
                    ? ` — ${deletingContract.shortName}`
                    : ""}
                  ) ? Cette action est irréversible.
                </Modal.Description>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  outline
                  onClick={() => setDeleteModalOpen(false)}
                  disabled={isDeleting}
                >
                  Annuler
                </Button>
                <Button
                  color="red"
                  onClick={confirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Suppression..." : "Supprimer"}
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </>
  );
}

export type ContractsListProps = Record<string, never>;
